import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { usePatients, usePlans, usePayers, useFeeSchedule } from '../api/hooks';
import { money, money2 } from '../lib/data';
import PageHeader from '../components/PageHeader';
import AgentChip from '../components/AgentChip';
import Confidence from '../components/Confidence';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calculator, Loader2 } from 'lucide-react';
import { toast } from '../lib/aiToast';

const CPT_OPTIONS = [
  { code: '99213', label: '99213 — Office visit, established (L3)' },
  { code: '99203', label: '99203 — Office visit, new (L3)' },
  { code: '17110', label: '17110 — Destruction of benign lesion' },
  { code: '11102', label: '11102 — Tangential biopsy' },
];

const CONF_VALUE = { exact: 0.96, contract: 0.85, estimate: 0.7 };
const CONF_LABEL = { exact: 'Exact — historical match', contract: 'Contract-based', estimate: '± 20% best inference' };

export default function Estimator() {
  const { session } = useAuth();
  const practiceId = session?.practice?.id || 'prac_001';
  const { data: patients = [] } = usePatients(practiceId);
  const { data: plans = [] } = usePlans();
  const { data: payers = [] } = usePayers();
  const { data: feeSchedule = [] } = useFeeSchedule(practiceId);
  const [patientId, setPatientId] = useState('');
  // Patients load async; fall back to the first one until the user picks.
  const selectedPatientId = patientId || patients[0]?.id || '';
  const [cpt, setCpt] = useState('99213');
  const [charge, setCharge] = useState('200');
  const [result, setResult] = useState(null);
  const [calc, setCalc] = useState(false);

  const compute = async () => {
    setCalc(true); setResult(null);
    await new Promise((r) => setTimeout(r, 650));
    const pat = patients.find((p) => p.id === selectedPatientId);
    const plan = plans.find((p) => p.id === pat?.planId);
    const fs = feeSchedule.find((f) => f.cpt === cpt);
    const rate = fs?.rate || parseFloat(charge) || 0;
    const copay = plan?.planType === 'PPO' ? 20 : plan?.planType === 'HMO' ? 30 : 25;
    const coins = plan?.planType === 'PPO' ? 0.1 : 0.2;
    const estimate = +(copay + (rate - copay) * coins).toFixed(2);
    let confidence = fs ? 'contract' : 'estimate';
    let explanation = fs
      ? `Contract-based: ${money2(rate)} contracted for CPT ${cpt} on ${plan?.planName}. Copay ${money(copay)} + ${(coins * 100).toFixed(0)}% coinsurance on the remainder.`
      : `Using charge ${money2(rate)} for CPT ${cpt}. Copay ${money(copay)} + ${(coins * 100).toFixed(0)}% coinsurance.`;
    if (cpt === '99213' && plan?.planType === 'PPO') { confidence = 'exact'; explanation = `Historical exact match: 14 prior visits on ${plan?.planName} + CPT ${cpt} averaged ${money2(estimate)} patient responsibility.`; }
    setResult({ estimate, confidence, explanation, rate, copay, coins });
    setCalc(false); toast.success('Estimate ready');
  };

  return (
    <div className="space-y-5 max-w-5xl">
      <PageHeader eyebrow="Intelligence" title="Good-faith estimator" subtitle="Combines 271, contracted rate, deductible status, and historical paid amounts into a collect-at-check-in recommendation." />

      <div className="grid md:grid-cols-2 gap-4">
        <div className="dh-card p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4">Inputs</div>
          <div className="space-y-4">
            <div>
              <Label>Patient</Label>
              <Select value={selectedPatientId} onValueChange={setPatientId}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.firstName} {p.lastName} — {payers.find((x) => x.id === p.payerId)?.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Procedure (CPT)</Label>
              <Select value={cpt} onValueChange={setCpt}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{CPT_OPTIONS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Practice charge ($)</Label><Input className="mt-1.5 tnum" value={charge} onChange={(e) => setCharge(e.target.value)} /></div>
            <Button className="w-full bg-brand-600 hover:bg-brand-700 text-white" onClick={compute} disabled={calc}>
              {calc ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Calculating…</> : <><Calculator className="h-4 w-4 mr-2" />Calculate estimate</>}
            </Button>
          </div>
        </div>

        <div className="dh-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm font-semibold text-slate-900">Recommendation</div>
            {result && <AgentChip agent="estimate" size="xs" />}
          </div>
          {!result && <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl p-8 text-center">Run a calculation to see the recommended collection amount and confidence band.</div>}
          {result && (
            <div className="space-y-4">
              <div>
                <div className="text-xs text-slate-500">Recommended collection</div>
                <div className="mt-1 text-5xl font-semibold text-slate-900 tnum tracking-tight">{money2(result.estimate)}</div>
                <div className="mt-2"><Confidence value={CONF_VALUE[result.confidence]} label={CONF_LABEL[result.confidence]} /></div>
              </div>
              <div className="ai-surface p-3 text-sm text-slate-700">{result.explanation}</div>
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div className="dh-inset p-3"><dt className="text-slate-500">Contracted rate</dt><dd className="font-semibold text-slate-900 mt-1 tnum">{money2(result.rate)}</dd></div>
                <div className="dh-inset p-3"><dt className="text-slate-500">Copay</dt><dd className="font-semibold text-slate-900 mt-1 tnum">{money(result.copay)}</dd></div>
                <div className="dh-inset p-3"><dt className="text-slate-500">Coinsurance</dt><dd className="font-semibold text-slate-900 mt-1 tnum">{(result.coins * 100).toFixed(0)}%</dd></div>
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
